import React from "react";
import { Pattern, MODULE_ACCENT } from "./Patterns";

// -------- Buttons ----------------------------------------------------------
const btnBase = "inline-flex items-center justify-center gap-2 rounded-xl font-medium ring-focus transition-colors disabled:opacity-50 disabled:pointer-events-none";
const btnSize = { sm: "h-8 px-3 text-xs", md: "h-10 px-4 text-sm", lg: "h-11 px-5 text-sm" };
const btnVar = {
  primary:   "bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm shadow-primary/20",
  secondary: "bg-transparent border border-primary/30 text-primary hover:bg-primary/5 dark:text-primary-foreground dark:border-primary/50 dark:hover:bg-primary/10",
  accent:    "bg-accent text-accent-foreground hover:bg-accent/90 shadow-sm shadow-accent/20",
  danger:    "bg-destructive text-destructive-foreground hover:bg-destructive/90",
  success:   "bg-success text-success-foreground hover:bg-success/90",
  ghost:     "hover:bg-muted/60 text-foreground",
  outline:   "border border-border bg-card hover:bg-muted/50 text-foreground",
};
export function Button({ variant = "primary", size = "md", className = "", children, ...p }) {
  return <button className={`${btnBase} ${btnSize[size]} ${btnVar[variant]} ${className}`} {...p}>{children}</button>;
}

// -------- Badge ------------------------------------------------------------
const badgeVar = {
  neutral:   "bg-muted text-muted-foreground",
  primary:   "bg-primary/10 text-primary dark:text-blue-300 border border-primary/20",
  success:   "bg-success/10 text-success dark:text-emerald-300 border border-success/20",
  healthy:   "bg-success/10 text-success dark:text-emerald-300 border border-success/20",
  completed: "bg-success/10 text-success dark:text-emerald-300 border border-success/20",
  approved:  "bg-success/10 text-success dark:text-emerald-300 border border-success/20",
  pending:   "bg-warning/10 text-warning dark:text-amber-300 border border-warning/20",
  draft:     "bg-muted text-muted-foreground border border-border",
  cancelled: "bg-muted text-muted-foreground line-through border border-border",
  delayed:   "bg-accent/10 text-accent border border-accent/20",
  critical:  "bg-destructive/10 text-destructive dark:text-red-300 border border-destructive/20",
  lowstock:  "bg-accent/10 text-accent border border-accent/20",
  ready:     "bg-success/10 text-success dark:text-emerald-300 border border-success/20",
  info:      "bg-primary/10 text-primary dark:text-blue-300 border border-primary/20",
};
export function Badge({ variant = "neutral", className = "", children, ...p }) {
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${badgeVar[variant] || badgeVar.neutral} ${className}`} {...p}>
      {children}
    </span>
  );
}

// -------- StatCard ---------------------------------------------------------
export function StatCard({ label, value, icon: Icon, tone = "primary", trend, hint, className = "", testid, children }) {
  const toneMap = {
    primary: "bg-primary/10 text-primary dark:text-blue-300",
    success: "bg-success/10 text-success dark:text-emerald-300",
    accent:  "bg-accent/10 text-accent",
    danger:  "bg-destructive/10 text-destructive dark:text-red-300",
    info:    "bg-primary/10 text-primary dark:text-blue-300",
    muted:   "bg-muted text-muted-foreground",
  };
  return (
    <div data-testid={testid} className={`kpi-card group ${className}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="eyebrow truncate">{label}</div>
          <div className="mt-2 font-heading text-3xl font-bold tracking-tight">{value}</div>
          {hint && <div className="subtle mt-1 truncate">{hint}</div>}
        </div>
        {Icon && (
          <div className={`shrink-0 h-11 w-11 grid place-items-center rounded-xl ${toneMap[tone] || toneMap.primary} group-hover:scale-105 transition-transform`}>
            <Icon className="w-5 h-5" />
          </div>
        )}
      </div>
      {trend && <div className="mt-3 text-xs font-medium">{trend}</div>}
      {children}
    </div>
  );
}

// -------- PageHero ---------------------------------------------------------
export function PageHero({ eyebrow, title, subtitle, moduleKind = "dashboard", actions, children }) {
  return (
    <div className="hero-premium p-6 sm:p-9 mb-6 animate-hero-in">
      <Pattern kind={moduleKind} />
      <div className="relative flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div className="min-w-0">
          {eyebrow && <div className="eyebrow mb-2">{eyebrow}</div>}
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">{title}</h1>
          {subtitle && <p className="mt-2 max-w-2xl">{subtitle}</p>}
          {children}
        </div>
        {actions && <div className="shrink-0 flex flex-wrap gap-2">{actions}</div>}
      </div>
    </div>
  );
}

// -------- Section / SectionShell ------------------------------------------
export function Section({ title, description, actions, className = "", children }) {
  return (
    <section className={`glass-card p-5 sm:p-6 ${className}`}>
      {(title || actions) && (
        <div className="flex items-center justify-between gap-3 mb-4">
          <div>
            {title && <h3 className="section-title text-lg">{title}</h3>}
            {description && <p className="subtle mt-0.5">{description}</p>}
          </div>
          {actions && <div className="flex items-center gap-2">{actions}</div>}
        </div>
      )}
      {children}
    </section>
  );
}

// -------- Data table wrapper (light polish only) ---------------------------
export function DataTable({ columns, rows, empty = "No data yet.", testid }) {
  return (
    <div className="overflow-x-auto -mx-2 sm:mx-0">
      <table data-testid={testid} className="w-full text-sm">
        <thead>
          <tr className="text-left">
            {columns.map((c, i) => (
              <th key={i} className="sticky top-0 bg-muted/60 backdrop-blur-sm px-3 py-2.5 text-[11px] uppercase tracking-wider font-semibold text-muted-foreground border-b border-border">
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan={columns.length} className="px-3 py-10 text-center subtle">{empty}</td></tr>
          )}
          {rows.map((r, i) => (
            <tr key={r.id ?? i} className={`transition-colors hover:bg-muted/40 ${i % 2 ? "bg-muted/20" : ""}`}>
              {columns.map((c, j) => (
                <td key={j} className="px-3 py-2.5 border-b border-border/60 text-foreground">
                  {c.cell ? c.cell(r) : r[c.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// -------- Input / Field ----------------------------------------------------
export function Field({ label, hint, error, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      {label && <span className="block text-xs font-medium text-muted-foreground mb-1.5">{label}</span>}
      {children}
      {hint && !error && <span className="block text-[11px] text-muted-foreground mt-1">{hint}</span>}
      {error && <span className="block text-[11px] text-destructive mt-1">{error}</span>}
    </label>
  );
}
export const inputCls = "w-full rounded-xl border border-input bg-card px-3.5 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/70 shadow-sm ring-focus focus-visible:border-ring transition-colors";

export function Input({ className = "", ...p }) { return <input className={`${inputCls} ${className}`} {...p} />; }
export function Textarea({ className = "", ...p }) { return <textarea className={`${inputCls} min-h-[90px] ${className}`} {...p} />; }
export function Select({ className = "", children, ...p }) { return <select className={`${inputCls} ${className}`} {...p}>{children}</select>; }

// -------- Empty state ------------------------------------------------------
export function EmptyState({ icon: Icon, title, description, action }) {
  return (
    <div className="rounded-2xl border border-dashed border-border p-10 text-center bg-card/50">
      {Icon && <div className="mx-auto h-12 w-12 grid place-items-center rounded-2xl bg-primary/10 text-primary mb-3"><Icon className="w-6 h-6" /></div>}
      <h3 className="font-heading text-lg font-semibold">{title}</h3>
      {description && <p className="subtle mt-1 max-w-md mx-auto">{description}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export { Pattern, MODULE_ACCENT };
